import { useEffect, useState } from 'react';

import { useSelectedAppContext } from '../../../contexts/SelectedAppContext';
import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubIconButton,
  DevHubModal,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { ValidationType } from './AppBulkEdit';
import { useAppBulkEditContext } from './context/AppBulkEditContext';

type AppBulkEditViewAppsProps = {
  setValidationHasError: (value: ValidationType) => void;
  toggleReset?: boolean;
};

interface AppRow {
  applicationId: number;
}

/**
 * Component for bulk editing apps.
 * @param props - The component props.
 * @param props.setValidationHasError - Function to set validation error state.
 */
const AppBulkEditViewApps = ({
  setValidationHasError,
  toggleReset,
}: AppBulkEditViewAppsProps) => {
  const { selectedApps, removeApp } = useSelectedAppContext();

  const { resetBulkEditContext } = useAppBulkEditContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const onRemoveClick = (row: AppRow) => {
    setSelectedAppId(row?.applicationId);
    setOpen(true);
  };

  const handleAddMore = () => {
    navigate('/my-apps');
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (selectedAppId) removeApp(selectedAppId);
    setOpen(false);
  };

  const validation = () => {
    const hasError = selectedApps?.length === 0;
    setValidationHasError({
      hasError: hasError,
      errorMessage: hasError ? 'Add an application to proceed' : '',
    });
  };

  useEffect(() => {
    if (toggleReset) {
      validation();
      resetBulkEditContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleReset]);

  useEffect(() => {
    validation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApps]);

  return (
    <>
      <DevHubGrid container direction="column" xs={8} alignItems="flex-end">
        <DevHubGrid item>
          <DevHubButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleAddMore}
          >
            Add more
          </DevHubButton>
        </DevHubGrid>
      </DevHubGrid>
      {/* <br/> */}
      <DevHubGrid item xs={8}>
        <DataTable
          value={selectedApps}
          dataKey="applicationId"
          paginator={false}
        >
          <Column field="applicationId" header="Application Id" />
          <Column field="name" header="Name" />
          <Column field="status" header="Status" />
          <Column
            header=""
            body={(rowData: AppRow) => (
              <DevHubIconButton onClick={() => onRemoveClick(rowData)}>
                <DevHubDynamicIcon iconName="Delete" color="error" />
              </DevHubIconButton>
            )}
          />
        </DataTable>
      </DevHubGrid>
      <DevHubModal open={open} onClose={handleCloseModal}>
        <DevHubGrid>
          <DevHubTypography>
            Do you want to remove this application
          </DevHubTypography>
          <DevHubBox display="flex" justifyContent="space-between">
            <DevHubButton
              variant="contained"
              color="primary"
              onClick={handleCloseModal}
            >
              Cancel
            </DevHubButton>
            <DevHubButton
              variant="contained"
              color="primary"
              onClick={handleConfirm}
            >
              Confirm
            </DevHubButton>
          </DevHubBox>
        </DevHubGrid>
      </DevHubModal>
    </>
  );
};

export default AppBulkEditViewApps;
